import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { LoggerService } from '../core/services/logger.service';
import { environment } from '../../environments/environment';

export interface LoginRequest {
    email: string;
    password: string;
}

export interface LoginResponse {
    access_token: string;
}

export interface User {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    isActive: boolean;
    clientId?: string;
    /** Nombre del cliente (desde JWT), para Torre de Control externa */
    clientName?: string;
    roles: string[];
    permissions: string[];
    modules?: string[]; // Códigos de módulos habilitados
}

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private readonly API_URL = `${environment.apiUrl}/v1`;
    private currentUserSubject = new BehaviorSubject<User | null>(null);
    public currentUser$ = this.currentUserSubject.asObservable();

    constructor(
        // eslint-disable-next-line no-unused-vars
        private http: HttpClient,
        // eslint-disable-next-line no-unused-vars
        private logger: LoggerService
    ) {
        // Verificar si hay un token almacenado al inicializar (solo en el navegador)
        if (typeof window !== 'undefined') {
            this.checkStoredToken();
        }
    }

    login(credentials: LoginRequest): Observable<LoginResponse> {
        return this.http
            .post<LoginResponse>(`${this.API_URL}/auth/login`, credentials)
            .pipe(
                tap((response) => {
                    // Almacenar el token (solo en el navegador)
                    if (typeof window !== 'undefined') {
                        localStorage.setItem(
                            'access_token',
                            response.access_token
                        );
                    }
                    // Decodificar y almacenar información del usuario
                    this.decodeAndStoreUser(response.access_token);
                })
            );
    }

    logout(): void {
        if (typeof window !== 'undefined') {
            // Tokens y usuarios (tanto claves nuevas como legacy)
            localStorage.removeItem('access_token');
            localStorage.removeItem('token');
            localStorage.removeItem('currentUser');
            // Estados y viewstate de módulos
            localStorage.removeItem('liquidaciones_vd_state');
            localStorage.removeItem('liquidaciones_agencia_state');
            // Email guardado para "Recordarme"
            localStorage.removeItem('remembered_email');
        }
        this.currentUserSubject.next(null);
    }

    isAuthenticated(): boolean {
        if (typeof window !== 'undefined') {
            return !!localStorage.getItem('access_token');
        }
        return false;
    }

    getToken(): string | null {
        if (typeof window !== 'undefined') {
            return localStorage.getItem('access_token');
        }
        return null;
    }

    getCurrentUser(): User | null {
        return this.currentUserSubject.value;
    }

    private checkStoredToken(): void {
        if (typeof window !== 'undefined') {
            const token = localStorage.getItem('access_token');
            if (token) {
                this.decodeAndStoreUser(token);
            }
        }
    }

    private decodeAndStoreUser(token: string): void {
        try {
            // Decodificar el token JWT (solo la parte del payload)
            const payload = JSON.parse(atob(token.split('.')[1]));

            const user: User = {
                id: payload.userId,
                email: payload.email,
                firstName: this.decodeUtf8(payload.firstName) || '',
                lastName: this.decodeUtf8(payload.lastName) || '',
                isActive: true,
                clientId: payload.clientId != null ? String(payload.clientId) : undefined,
                clientName: payload.clientName,
                roles: payload.roles || [],
                permissions: payload.permissions || [],
                modules: payload.modules || [], // Códigos de módulos habilitados
            };

            this.currentUserSubject.next(user);
        } catch (error) {
            this.logger.error(
                'Error decodificando token',
                error as Error,
                'AuthService'
            );
            this.logout();
        }
    }

    /**
     * Decodifica caracteres UTF-8 que pueden estar mal codificados
     */
    private decodeUtf8(str: string): string {
        if (!str) return '';

        try {
            // Intentar decodificar caracteres especiales
            return decodeURIComponent(escape(str));
        } catch (e) {
            // Si falla, devolver la cadena original
            return str;
        }
    }

    hasPermission(permission: string): boolean {
        const user = this.getCurrentUser();
        return user?.permissions.includes(permission) || false;
    }

    hasRole(role: string): boolean {
        const user = this.getCurrentUser();
        return user?.roles.includes(role) || false;
    }

    hasModule(moduleCode: string): boolean {
        const user = this.getCurrentUser();
        return user?.modules?.includes(moduleCode) || false;
    }

    /**
     * Refrescar token JWT con información actualizada del usuario
     * Útil cuando se actualizan módulos, roles o permisos
     */
    refreshToken(): Observable<LoginResponse> {
        return this.http
            .get<LoginResponse>(`${this.API_URL}/auth/refresh`, {
                headers: {
                    Authorization: `Bearer ${this.getToken()}`,
                },
            })
            .pipe(
                tap((response) => {
                    // Actualizar el token almacenado
                    if (typeof window !== 'undefined') {
                        localStorage.setItem(
                            'access_token',
                            response.access_token
                        );
                    }
                    // Decodificar y actualizar información del usuario
                    this.decodeAndStoreUser(response.access_token);
                })
            );
    }
}
